import { Box, IconButton, Typography, useTheme } from "@material-ui/core";
import { endOfMonth, isMonday, isSunday, startOfMonth } from "date-fns";
import { calendarBackground } from "../../hooks/use-map-theme-styles";
import { Maybe } from "../../models/generated/graphql";
import formatValidDate from "../../utils/format-valid-date";

function getColor(isSelected: boolean, isSelectedStartOrEnd: boolean, isWithinHoverRange: boolean, isDisabled: boolean) {
	return ({ selectedFirstOrLastColor, normalColor, selectedColor, rangeHoverColor, disabledColor }: any) => {
		if (isSelectedStartOrEnd) {
			return selectedFirstOrLastColor;
		} else if (isSelected) {
			return selectedColor;
		} else if (isWithinHoverRange) {
			return rangeHoverColor;
		} else if (isDisabled) {
			return disabledColor;
		} else {
			return normalColor;
		}
	};
}

interface DayProps {
	dayLabel: string;
	date: Date;
	startDate?: Maybe<Date>;
	endDate?: Maybe<Date>;
	hoveredDate?: Maybe<Date>;
	handleSelectDay: (date: Date) => void;
}

const Day: React.FC<DayProps> = ({ dayLabel, date, startDate, endDate, handleSelectDay }) => {
	const { spacing, palette } = useTheme();

	if (!dayLabel) {
		return <div />;
	}

	const isDayMonday = isMonday(date);
	const isDaySunday = isSunday(date);
	const isStartOfMonthDay = date.getTime() === startOfMonth(date).getTime();
	const isEndOfMonthDay = date.getTime() === endOfMonth(date).getTime();
	const isSelected = (!!startDate || !!endDate) && (!startDate || date >= startDate) && (!endDate || date <= endDate);
	const isSelectedStart = date.getTime() === startDate?.getTime();
	const isSelectedEnd = date.getTime() === endDate?.getTime();
	const isWithinHoverRange = false;
	const disabledDate = false;

	const getColorFn = getColor(isSelected, isSelectedStart || isSelectedEnd, isWithinHoverRange, disabledDate);

	const getCalendarRadius = () => {
		if (!isSelected) {
			return;
		}

		const startRadius = "20px 0px 0px 20px";
		const endRadius = "0px 20px 20px 0px";
		if (startDate === endDate) return "20px";
		if (isSelectedStart) return startRadius;
		if (isSelectedEnd) return endRadius;
		if (isDayMonday || isStartOfMonthDay) return startRadius;
		if (isDaySunday || isEndOfMonthDay) return endRadius;
		return;
	};

	return (
		<Box
			style={{
				justifyContent: "center",
				alignItems: "center",
				display: "flex",
				padding: "0 2px",
				marginBottom: spacing(0.5),
				background: getColorFn({
					selectedColor: calendarBackground,
					rangeHoverColor: calendarBackground,
					selectedFirstOrLastColor: calendarBackground,
				}),
				borderRadius: getCalendarRadius(),
			}}
		>
			<IconButton
				title={formatValidDate(date) || ""}
				size="small"
				onClick={() => handleSelectDay(date)}
				style={{
					height: "28px",
					width: "28px",
					color: getColorFn({
						normalColor: "black",
						disabledColor: "#808285",
						selectedFirstOrLastColor: palette.primary.contrastText,
					}),
					background: getColorFn({
						selectedFirstOrLastColor: palette.primary.main,
						disabledColor: palette.background.paper,
					}),
				}}
			>
				<Typography
					variant="caption"
					style={{
						paddingLeft: spacing(0.2),
						paddingRight: spacing(0.2),
					}}
				>
					{dayLabel}
				</Typography>
			</IconButton>
		</Box>
	);
};

export default Day;
